import { X } from "lucide-react"
import { useCallback, useEffect, useMemo, useState } from "react"

import { bookCache } from "../modules/image-cache/book-cache"
import { Button } from "../shared-components/button"

const CHARS_PER_PAGE = 500

export interface BookNavigatorProps {
  bookIndex: string
  onClose: ()=> void
}

export function BookNavigator(input: BookNavigatorProps) {
  const { bookIndex, onClose } = input
  const [data, setData] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<null | string>(null)
  const [page, setPage] = useState(0)

  useEffect(()=> {
    setLoading(true)
    setError(null)
    bookCache
      .getBookData(bookIndex)
      .then(setData)
      .catch(e=> {
        setError(e instanceof Error ? e.message : "Failed to load book")
        setData([])
      })
      .finally(()=> setLoading(false))
  }, [bookIndex])

  const pages = useMemo(()=> {
    if (data.length === 0) return []
    const text = data.join(" ")
    const result: string[] = []
    let start = 0
    while (start < text.length) {
      let end = start + CHARS_PER_PAGE
      if (end >= text.length) {
        result.push(text.slice(start))
        break
      }
      const lastSpace = text.lastIndexOf(" ", end)
      if (lastSpace > start) {
        end = lastSpace
      }
      result.push(text.slice(start, end))
      start = end + 1
    }
    return result
  }, [data])

  const totalPages = pages.length
  const clampedPage = Math.min(page, Math.max(totalPages - 1, 0))

  const goNext = useCallback(()=> {
    setPage(p=> Math.min(p + 1, totalPages - 1))
  }, [totalPages])

  const goPrev = useCallback(()=> {
    setPage(p=> Math.max(p - 1, 0))
  }, [])

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="
          rounded-lg bg-white p-6
          dark:bg-zinc-800
        ">
          Loading book content…
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="
          relative rounded-lg bg-white p-6
          dark:bg-zinc-800
        ">
          <button
            aria-label="Close"
            className="absolute top-2 right-2 cursor-pointer border-none bg-transparent p-1"
            onClick={onClose}
          >
            <X size={20} />
          </button>
          <div className="text-destructive">{error}</div>
        </div>
      </div>
    )
  }

  if (totalPages === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="
          relative rounded-lg bg-white p-6
          dark:bg-zinc-800
        ">
          <button
            aria-label="Close"
            className="absolute top-2 right-2 cursor-pointer border-none bg-transparent p-1"
            onClick={onClose}
          >
            <X size={20} />
          </button>
          <div>No content available</div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="
        relative mx-auto flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg
        bg-white p-6 shadow-xl
        dark:bg-zinc-800
      ">
        <button
          aria-label="Close book navigator"
          className="absolute top-2 right-2 z-10 cursor-pointer border-none bg-transparent p-1"
          onClick={onClose}
        >
          <X size={20} />
        </button>

        <div className="mt-6 mb-4 flex-1 overflow-y-auto text-lg/relaxed text-foreground">
          {pages[clampedPage] ?? ""}
        </div>

        <div className="flex items-center justify-between border-t pt-4">
          <Button
            disabled={clampedPage === 0}
            onClick={goPrev}
            variant="outline"
          >
            Previous
          </Button>
          <span className="text-muted-foreground text-sm">
            Page {clampedPage + 1} / {totalPages}
          </span>
          <Button
            disabled={clampedPage >= totalPages - 1}
            onClick={goNext}
            variant="outline"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}
